import { TriggerStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { triggerKeySupportsSmartSending } from "@redotech/redo-flows-common/automation-utils";
import { useTranslation } from "@redotech/redo-i18n";
import { useDefaultRuleEditContext } from "@redotech/redo-merchant-support-app/setting/rules/default-rule-edit-context";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { PartialWithRequiredType, StepTypeDetailsProps } from "../../steps";

export function ShouldSkipSmartSendingInspector({
  step,
  setStep,
  readonly,
}: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>) {
  const { t } = useTranslation();
  // Trigger is fixed in default-rule templates.
  const isDefaultRuleEdit = useDefaultRuleEditContext() !== null;

  if (readonly) {
    // Read-only flow views must not render an editable toggle; the change
    // would mutate editor state with no save path.
    return null;
  }
  if (!step.key) {
    // should never happen
    return null;
  }
  if (!triggerKeySupportsSmartSending(step.key)) {
    // Smart sending only applies to messaging flows; every other trigger,
    // including any newly added key, hides the toggle by default.
    return null;
  }
  return (
    <Flex dir="column" gap="md">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        {t("Smart sending")}
      </Text>
      <Text fontSize="xxs" textColor="secondary">
        {t(
          "By default, most automations will skip sending a message if we have recently sent a message via the same channel to that customer. Bypassing that protection may result in excessive sending.",
        )}
      </Text>
      <RedoToggle
        disabled={isDefaultRuleEdit}
        label={t("Bypass smart sending protection")}
        setValue={(value) => {
          if (isDefaultRuleEdit) {
            return;
          }
          setStep({ ...step, shouldSkipSmartSending: value });
        }}
        size="xs"
        value={step.shouldSkipSmartSending ?? false}
      />
    </Flex>
  );
}
